import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { CameraMode } from '@visualizer/types';
import usePersistentStore, { useGlobalBucket } from '@gui/store/persistent/persistentStore';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import Grid from 'Geodata/grid';

type Corner = 'bl' | 'br' | 'tl' | 'tr';

type OverlayPovSensorWarningProps = {
    corner?: Corner;
};

const OverlayPovSensorWarning: React.FC<OverlayPovSensorWarningProps> = ({ corner = 'tr' }) => {
    const currentCameraType = useGlobalBucket((s) => s.ambient.currentCamera.type);
    const simulationItems = usePersistentStore((s) => s.getActiveLayerState()?.simulationItems);
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const defaultCoords = React.useMemo(() => Grid.indexToXYZ(Grid.firstActiveID), []);
    const { x, y, z } = simulationItems ?? { x: defaultCoords[0], y: defaultCoords[1], z: defaultCoords[2] };
    const activeSensorIndex = Grid.xyzToIndex(x, y, z);
    const sensorPoint = activeSensorIndex >= 0 ? Grid.sensorPoints[activeSensorIndex] : Grid.sensorPoints[0];
    const activeSensorId = sensorPoint.getID();
    const isCurrentSensorValid = Boolean(sensorPoint && activeSensorId !== -1);
    const showWarning = currentCameraType === CameraMode.POV && !isCurrentSensorValid;

    if (!showWarning) return null;

    const columnBg = alpha(theme.palette.background.default, isLight ? 0.45 : 0.3);
    const panelBg = alpha(theme.palette.background.paper, isLight ? 0.26 : 0.5);
    const panelBorder = alpha(theme.palette.error.main, isLight ? 0.45 : 0.6);
    const iconColor = theme.palette.error.main;
    const warningText = 'Move selected Sensor Point inside the Grid';
    const tooltipPlacement = corner === 'bl' || corner === 'tl' ? 'right' : 'left';
    const basePos: React.CSSProperties =
        corner === 'br'
            ? { right: 'calc(16px + var(--right-drawer-visible, 0px))', bottom: 16 }
            : corner === 'tr'
              ? { right: 'calc(16px + var(--right-drawer-visible, 0px))', top: 16 }
              : corner === 'bl'
                ? { left: 16, bottom: 16 }
                : { left: 16, top: 16 };

    return (
        <div
            style={{
                position: 'fixed',
                zIndex: 2000,
                pointerEvents: 'none',
                ...basePos,
            }}
        >
            <div
                style={{
                    display: 'inline-block',
                    borderRadius: 14,
                    background: columnBg,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    padding: 0,
                }}
            >
                <CustomTooltip
                    title={warningText}
                    placement={tooltipPlacement}
                    popperSx={{ zIndex: 24000 }}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: { offset: [0, 8] },
                                },
                            ],
                        },
                    }}
                >
                    <div
                        className="data-panel"
                        style={{
                            width: 44,
                            height: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 12,
                            background: panelBg,
                            border: `1px solid ${panelBorder}`,
                            color: iconColor,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            pointerEvents: 'auto',
                        }}
                        aria-label={warningText}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M9.75 20.7501L11.223 21.5684C11.5066 21.726 11.6484 21.8047 11.7986 21.8356C11.9315 21.863 12.0685 21.863 12.2015 21.8356C12.3516 21.8047 12.4934 21.726 12.777 21.5684L14.25 20.7501M5.25 18.2501L3.82297 17.4573C3.52346 17.2909 3.37368 17.2077 3.26463 17.0893C3.16816 16.9847 3.09515 16.8606 3.05048 16.7254C3 16.5726 3 16.4013 3 16.0586V14.5001M3 9.50009V7.94153C3 7.59889 3 7.42757 3.05048 7.27477C3.09515 7.13959 3.16816 7.01551 3.26463 6.91082C3.37368 6.79248 3.52345 6.70928 3.82297 6.54288L5.25 5.75009M9.75 3.25008L11.223 2.43177C11.5066 2.27421 11.6484 2.19543 11.7986 2.16454C11.9315 2.13721 12.0685 2.13721 12.2015 2.16454C12.3516 2.19543 12.4934 2.27421 12.777 2.43177L14.25 3.25008M18.75 5.75008L20.177 6.54288C20.4766 6.70928 20.6263 6.79248 20.7354 6.91082C20.8318 7.01551 20.9049 7.13959 20.9495 7.27477C21 7.42757 21 7.59889 21 7.94153V9.50008M21 14.5001V16.0586C21 16.4013 21 16.5726 20.9495 16.7254C20.9049 16.8606 20.8318 16.9847 20.7354 17.0893C20.6263 17.2077 20.4766 17.2909 20.177 17.4573L18.75 18.2501M9.75 10.7501L12 12.0001M12 12.0001L14.25 10.7501M12 12.0001V14.5001M3 7.00008L5.25 8.25008M18.75 8.25008L21 7.00008M12 19.5001V22.0001"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </CustomTooltip>
            </div>
        </div>
    );
};

export default OverlayPovSensorWarning;
